// Parses `docker pull` stdout into structured per-layer progress.
// Pure function — no DOM, no React. Used by PullProgress.tsx.

export type LayerStatus =
  | 'pulling' // "Pulling fs layer"
  | 'downloading' // "Downloading [...] X/Y"
  | 'verifying' // "Verifying Checksum"
  | 'download_done' // "Download complete"
  | 'extracting' // "Extracting [...] X/Y"
  | 'done' // "Pull complete"

export interface LayerProgress {
  id: string
  status: LayerStatus
  progress?: { current: number; total: number } // bytes
}

export interface ParsedPullOutput {
  layers: LayerProgress[]
  complete: boolean
}

const STATUS_PATTERNS: Array<[RegExp, LayerStatus]> = [
  [/Pulling fs layer/, 'pulling'],
  [/Downloading\b/, 'downloading'],
  [/Verifying Checksum/, 'verifying'],
  [/Download complete/, 'download_done'],
  [/Extracting\b/, 'extracting'],
  [/Pull complete/, 'done'],
]

const SIZE_RE = /(\d+(?:\.\d+)?)\s*(GB|MB|KB|B)/gi

const UNIT_BYTES: Record<string, number> = {
  B: 1,
  KB: 1024,
  MB: 1024 * 1024,
  GB: 1024 * 1024 * 1024,
}

function parseSize(token: string): number {
  const m = /^(\d+(?:\.\d+)?)\s*(GB|MB|KB|B)$/i.exec(token.trim())
  if (!m) return NaN
  return parseFloat(m[1]) * UNIT_BYTES[m[2].toUpperCase()]
}

function extractProgress(line: string): { current: number; total: number } | undefined {
  // "Downloading [...] 47.2MB/312MB" or "Extracting [...] 5.1MB/312MB"
  const m = /(\d+(?:\.\d+)?\s*(?:GB|MB|KB|B))\s*\/\s*(\d+(?:\.\d+)?\s*(?:GB|MB|KB|B))/i.exec(line)
  if (!m) return undefined
  const current = parseSize(m[1])
  const total = parseSize(m[2])
  if (Number.isNaN(current) || Number.isNaN(total)) return undefined
  return { current, total }
}

const LAYER_LINE_RE = /^(\S+):\s+(.*)$/

export function parseDockerPullOutput(input: string): ParsedPullOutput {
  if (!input) return { layers: [], complete: false }

  const layerMap = new Map<string, LayerProgress>()
  const order: string[] = []
  let complete = false

  for (const rawLine of input.split('\n')) {
    const line = rawLine.replace(/\r/g, '').trimEnd()
    if (!line) continue

    if (/^Status:/.test(line)) {
      if (/Downloaded newer image|Image is up to date/.test(line)) {
        complete = true
      }
      continue
    }

    const m = LAYER_LINE_RE.exec(line)
    if (!m) continue

    const id = m[1]
    const rest = m[2]

    // Skip the "<image>: Pulling from <repo>" header line (not a layer).
    if (/Pulling from /.test(rest)) continue

    let status: LayerStatus | null = null
    for (const [pat, st] of STATUS_PATTERNS) {
      if (pat.test(rest)) {
        status = st
        break
      }
    }
    if (!status) continue

    if (!layerMap.has(id)) {
      order.push(id)
      layerMap.set(id, { id, status })
    }
    const layer = layerMap.get(id)!
    layer.status = status
    const progress = extractProgress(rest)
    if (progress) layer.progress = progress
  }

  return {
    layers: order.map(id => layerMap.get(id)!),
    complete,
  }
}

// ── Display helpers ─────────────────────────────────────────────────────

export const LAYER_STATUS_LABEL: Record<LayerStatus, string> = {
  pulling: 'Aguardando',
  downloading: 'Baixando',
  verifying: 'Verificando',
  download_done: 'Baixado',
  extracting: 'Extraindo',
  done: 'Pronto',
}

export function formatBytes(b: number): string {
  if (b >= 1024 * 1024 * 1024) return `${(b / 1024 / 1024 / 1024).toFixed(1)} GB`
  if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`
  if (b >= 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${b} B`
}
